import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
  LinkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { PageMeta } from '@common/components/core';
import { Button, Card, CardHeader, Badge, Alert } from '@common/components/ui';
import { instagramService } from '../services/instagramService';

const breadcrumbs = [
  { label: 'Dashboard', href: '/' },
  { label: 'Instagram' },
];

const InstagramIntegration: React.FC = () => {
  const queryClient = useQueryClient();

  const { data: connection, isLoading } = useQuery({
    queryKey: ['instagramStatus'],
    queryFn: () => instagramService.getStatus(),
    select: (res) => res.data,
  });

  const connectMutation = useMutation({
    mutationFn: () => instagramService.getConnectUrl(),
    onSuccess: (res) => {
      // Full redirect rather than a popup: Meta's login blocks framing, and the
      // OAuth callback lands back on the API host.
      window.location.href = res.data.url;
    },
    onError: () => toast.error('Could not start the Instagram connection'),
  });

  const refreshMutation = useMutation({
    mutationFn: () => instagramService.refreshToken(),
    onSuccess: () => {
      toast.success('Access token renewed for another 60 days');
      queryClient.invalidateQueries({ queryKey: ['instagramStatus'] });
    },
    onError: () => toast.error('Refresh failed — you may need to reconnect'),
  });

  const disconnectMutation = useMutation({
    mutationFn: () => instagramService.disconnect(),
    onSuccess: () => {
      toast.success('Instagram account disconnected');
      queryClient.invalidateQueries({ queryKey: ['instagramStatus'] });
    },
    onError: () => toast.error('Failed to disconnect'),
  });

  if (isLoading) {
    return (
      <>
        <PageMeta title="Instagram" breadcrumbs={breadcrumbs} />
        <Card className="animate-pulse">
          <div className="h-6 w-1/3 bg-gray-200 dark:bg-gray-700 rounded mb-6" />
          <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded" />
        </Card>
      </>
    );
  }

  const isConnected = !!connection?.connected;
  const days = connection?.daysUntilExpiry;

  return (
    <>
      <PageMeta title="Instagram" breadcrumbs={breadcrumbs} />

      <div className="space-y-6 max-w-3xl">
        <Card>
          <CardHeader className="px-0 pt-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Instagram Direct Messages
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Connect the shop's Instagram account so the AI assistant can answer customer DMs.
            </p>
          </CardHeader>

          <div className="pt-5">

          {isConnected ? (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                {connection?.profilePictureUrl ? (
                  <img
                    src={connection.profilePictureUrl}
                    alt={connection.username}
                    className="h-16 w-16 rounded-full object-cover border border-gray-200 dark:border-gray-700"
                  />
                ) : (
                  <div className="h-16 w-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                    <ChatBubbleLeftRightIcon className="h-7 w-7 text-gray-400" />
                  </div>
                )}

                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                      @{connection?.username}
                    </p>
                    <Badge color="success">
                      <CheckCircleIcon className="h-3.5 w-3.5 mr-1" />
                      Connected
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {connection?.accountType}
                    {typeof connection?.followersCount === 'number' &&
                      ` · ${connection.followersCount.toLocaleString()} followers`}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                    ID {connection?.userId}
                  </p>
                </div>
              </div>

              {typeof days === 'number' && (
                <Alert variant={days < 14 ? 'warning' : 'info'}>
                  {days < 14 ? (
                    <>
                      This access token expires in <strong>{days} days</strong>. It renews
                      automatically every Monday, but you can renew it now if you'd rather not wait.
                    </>
                  ) : (
                    <>
                      Access token valid for <strong>{days} more days</strong>. It renews
                      automatically every Monday.
                    </>
                  )}
                </Alert>
              )}

              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => refreshMutation.mutate()}
                  loading={refreshMutation.isPending}
                  icon={<ArrowPathIcon className="h-4 w-4" />}
                  variant="secondary"
                >
                  Renew token
                </Button>
                <Button
                  onClick={() => connectMutation.mutate()}
                  loading={connectMutation.isPending}
                  icon={<LinkIcon className="h-4 w-4" />}
                  variant="secondary"
                >
                  Reconnect a different account
                </Button>
                <Button
                  onClick={() => disconnectMutation.mutate()}
                  loading={disconnectMutation.isPending}
                  icon={<TrashIcon className="h-4 w-4" />}
                  variant="danger"
                >
                  Disconnect
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <ChatBubbleLeftRightIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Instagram account connected
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
                Connect an Instagram professional account. Once connected, customer DMs are
                answered automatically by the shopping assistant.
              </p>
              <Button
                onClick={() => connectMutation.mutate()}
                loading={connectMutation.isPending}
                icon={<LinkIcon className="h-4 w-4" />}
              >
                Connect Instagram account
              </Button>
            </div>
          )}
          </div>
        </Card>

        {isConnected && connection?.source === 'env' && (
          <Alert variant="warning">
            <ExclamationTriangleIcon className="h-4 w-4 inline mr-1" />
            This token comes from the server's environment file, so it cannot be renewed
            automatically. Use <strong>Reconnect</strong> above to store a renewable one.
          </Alert>
        )}
      </div>
    </>
  );
};

export default InstagramIntegration;
